import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Loader2, Plus, Users } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { leadListsApi } from '@/api/leads';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { Field } from '@/components/ui/field';
import { Loader } from '@/components/ui/loader';
import { Modal } from '@/components/ui/modal';
import { Topbar } from '@/components/ui/topbar';
import { ApiError } from '@/lib/api-client';
import { newLeadListSchema, type NewLeadListInput } from '@/lib/form-schemas';
import { qk } from '@/lib/query-keys';

export function LeadsPage(): JSX.Element {
  const [newOpen, setNewOpen] = useState(false);
  const list = useQuery({
    queryKey: qk.leadLists.all,
    queryFn: () => leadListsApi.list(),
  });

  const lists = list.data ?? [];

  return (
    <>
      <Topbar
        title="Leads"
        description={`${lists.length} lead list${lists.length === 1 ? '' : 's'}`}
        actions={
          <button className="mb-btn-primary" onClick={() => setNewOpen(true)}>
            <Plus className="h-4 w-4" strokeWidth={2} />
            New list
          </button>
        }
      />
      <div className="flex-1 overflow-auto p-6">
        {list.isLoading ? (
          <div className="flex justify-center py-10">
            <Loader />
          </div>
        ) : list.isError ? (
          <ErrorState onRetry={() => void list.refetch()} />
        ) : lists.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No lead lists yet"
            description="Create a list and upload a CSV to start."
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {lists.map((l) => (
              <Link
                key={l.id}
                to={`/leads/${l.id}`}
                className="mb-card p-4 group hover:border-mb-accent transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium text-mb-text group-hover:text-mb-accent-press">
                    {l.name}
                  </div>
                  <ArrowRight className="h-4 w-4 text-mb-text-muted group-hover:text-mb-accent-press" />
                </div>
                {l.description ? (
                  <div className="mt-1 text-xs text-mb-text-muted line-clamp-2">
                    {l.description}
                  </div>
                ) : null}
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="text-2xl font-semibold text-mb-text">{l.total_leads}</span>
                  <span className="text-xs text-mb-text-muted">
                    lead{l.total_leads === 1 ? '' : 's'}
                    {l.main_vertical ? ` · ${l.main_vertical}` : ''}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <NewListModal open={newOpen} onClose={() => setNewOpen(false)} />
    </>
  );
}

function NewListModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<NewLeadListInput>({
    resolver: zodResolver(newLeadListSchema),
    defaultValues: { name: '', description: '' },
  });

  const create = useMutation({
    mutationFn: (v: NewLeadListInput) => leadListsApi.create({ name: v.name, description: v.description || undefined }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.leadLists.all });
      form.reset();
      onClose();
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await create.mutateAsync(values);
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : 'Create failed');
    }
  });

  return (
    <Modal open={open} onClose={onClose} title="New lead list" size="sm">
      <form onSubmit={onSubmit} className="space-y-3" noValidate>
        <Field label="Name" required error={form.formState.errors.name?.message}>
          <input type="text" className="mb-input" {...form.register('name')} />
        </Field>
        <Field label="Description" error={form.formState.errors.description?.message}>
          <textarea rows={3} className="mb-input" {...form.register('description')} />
        </Field>
        {submitError ? (
          <div role="alert" className="rounded-md border border-mb-danger/30 bg-mb-danger-soft p-2 text-xs text-mb-danger">
            {submitError}
          </div>
        ) : null}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="mb-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="mb-btn-primary"
            disabled={create.isPending || form.formState.isSubmitting}
          >
            {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Create list
          </button>
        </div>
      </form>
    </Modal>
  );
}
